import { useEffect, useRef, useState } from 'react'
import './LumaScope.css'

function LumaScope({ videoRef, imageRef, useSMPTE = false, seekTime = 0, is2X = false, onToggle2X }) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const animationFrameRef = useRef(null)
  const [isActive, setIsActive] = useState(true)
  const [brightness, setBrightness] = useState(15)
  const [rulerY, setRulerY] = useState(null) // Y position of ruler line
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })
  const lastSeekTimeRef = useRef(seekTime)
  const lastFrameTimeRef = useRef(0)
  const tempCanvasRef = useRef(null)
  const TARGET_FPS = 30
  const FRAME_INTERVAL = 1000 / TARGET_FPS

  // Resize canvas to fit container
  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect()
      const headerHeight = 60
      const availableHeight = rect.height - headerHeight
      const availableWidth = rect.width - 32
      
      const newWidth = Math.floor(availableWidth)
      const newHeight = Math.floor(Math.max(200, availableHeight))
      
      // Only update if size changed
      if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width = newWidth
        canvas.height = newHeight
        setCanvasSize({ width: newWidth, height: newHeight })
      }
    }

    resizeCanvas()
    const resizeObserver = new ResizeObserver(resizeCanvas)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const video = useSMPTE ? null : videoRef.current
    const image = useSMPTE ? imageRef.current : null
    const source = useSMPTE ? image : video
    if (!canvas || !source) return

    if (!tempCanvasRef.current) {
      tempCanvasRef.current = document.createElement('canvas')
    }

    lastSeekTimeRef.current = seekTime

    const ctx = canvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
      willReadFrequently: false
    })
    const width = canvas.width
    const height = canvas.height

    const drawLumaScope = (currentTime = performance.now()) => {
      const seekChanged = seekTime !== lastSeekTimeRef.current
      const isVideoReady = !useSMPTE && video && video.readyState >= 2 && !video.paused
      const isImageReady = useSMPTE && image && image.complete
      const shouldUpdate = isActive && (isVideoReady || isImageReady || seekChanged)

      const timeSinceLastFrame = currentTime - lastFrameTimeRef.current
      const shouldDraw = shouldUpdate && (seekChanged || timeSinceLastFrame >= FRAME_INTERVAL)

      if (!shouldDraw) {
        animationFrameRef.current = requestAnimationFrame(drawLumaScope)
        return
      }

      if (seekChanged) {
        lastSeekTimeRef.current = seekTime
      }
      lastFrameTimeRef.current = currentTime

      // Clear canvas
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, width, height)

      // Get source dimensions
      const sourceWidth = useSMPTE ? (image.naturalWidth || 640) : (video.videoWidth || 640)
      const sourceHeight = useSMPTE ? (image.naturalHeight || 360) : (video.videoHeight || 360)

      const tempCanvas = tempCanvasRef.current
      if (tempCanvas.width !== sourceWidth || tempCanvas.height !== sourceHeight) {
        tempCanvas.width = sourceWidth
        tempCanvas.height = sourceHeight
      }

      const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: false })
      tempCtx.drawImage(source, 0, 0, sourceWidth, sourceHeight)
      const imageData = tempCtx.getImageData(0, 0, sourceWidth, sourceHeight)
      const data = imageData.data

      // Luma scope: X-axis = horizontal position, Y-axis = luma value
      const scopeWidth = width
      const scopeHeight = height
      const lumaLevels = 256

      // Histogram for luma values
      const lumaHistogram = new Array(lumaLevels).fill(0).map(() => new Uint32Array(scopeWidth))

      // Process pixels
      const sampleStep = Math.max(1, Math.floor(sourceHeight / 150))
      const invSourceWidth = 1 / sourceWidth

      for (let imageX = 0; imageX < sourceWidth; imageX++) {
        const scopeX = Math.floor(imageX * invSourceWidth * scopeWidth)

        if (scopeX >= 0 && scopeX < scopeWidth) {
          for (let imageY = 0; imageY < sourceHeight; imageY += sampleStep) {
            const idx = (imageY * sourceWidth + imageX) << 2

            if (idx + 2 < data.length) {
              // Calculate luma using Rec. 709 coefficients: Y = 0.2126*R + 0.7152*G + 0.0722*B
              // (Rec. 709 is standard for HD video, Rec. 601 is for SD)
              const r = data[idx]
              const g = data[idx + 1]
              const b = data[idx + 2]
              const luma = Math.floor(0.2126 * r + 0.7152 * g + 0.0722 * b)

              if (luma < lumaLevels) {
                lumaHistogram[luma][scopeX]++
              }
            }
          }
        }
      }

      // Find max for normalization
      let maxCount = 0
      for (let luma = 0; luma < lumaLevels; luma++) {
        for (let x = 0; x < scopeWidth; x++) {
          if (lumaHistogram[luma][x] > maxCount) {
            maxCount = lumaHistogram[luma][x]
          }
        }
      }

      // Draw luma scope
      const invLumaLevels = 1 / lumaLevels
      const invMaxCount = maxCount > 0 ? 1 / maxCount : 0
      ctx.fillStyle = '#ffffff'
      ctx.globalAlpha = 1

      for (let luma = 0; luma < lumaLevels; luma++) {
        const y = scopeHeight - 1 - Math.floor(luma * invLumaLevels * scopeHeight)

        for (let x = 0; x < scopeWidth; x++) {
          const count = lumaHistogram[luma][x]
          if (count > 0) {
            const alpha = Math.min(1, count * invMaxCount * brightness)
            if (alpha > 0.01) {
              ctx.globalAlpha = alpha
              ctx.fillRect(x, y, 1, 1)
            }
          }
        }
      }

      ctx.globalAlpha = 1

      // Draw pixel value reference lines (converted from IRE scale: 0, 7.5, 20, 40, 60, 80, 100)
      const ireLevels = [0, 7.5, 20, 40, 60, 80, 100]
      const keyPixelValues = ireLevels.map(ire => Math.round((ire / 100) * 255))
      ctx.setLineDash([2, 2])

      keyPixelValues.forEach((pixel, index) => {
        const y = scopeHeight - 1 - Math.floor((pixel / 255) * scopeHeight)

        if (pixel === 0 || pixel === 255) {
          ctx.strokeStyle = '#888'
          ctx.lineWidth = 1.5
        } else if (ireLevels[index] === 7.5) {
          ctx.strokeStyle = '#ffff00'
          ctx.lineWidth = 2
        } else {
          ctx.strokeStyle = '#555'
          ctx.lineWidth = 1
        }

        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(scopeWidth, y)
        ctx.stroke()
      })

      ctx.setLineDash([])
      ctx.lineWidth = 1

      // Draw additional IRE lines at 70% opacity (10, 30, 50, 70, 90)
      const additionalIreLevels = [10, 30, 50, 70, 90]
      const additionalPixelValues = additionalIreLevels.map(ire => Math.round((ire / 100) * 255))
      ctx.setLineDash([4, 4])
      ctx.globalAlpha = 0.7
      ctx.strokeStyle = '#555'
      ctx.lineWidth = 1

      additionalPixelValues.forEach(pixel => {
        const y = scopeHeight - 1 - Math.floor((pixel / 255) * scopeHeight)
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(scopeWidth, y)
        ctx.stroke()
      })

      ctx.setLineDash([])
      ctx.globalAlpha = 1

      // IRE labels on left (main lines)
      ctx.fillStyle = '#666'
      ctx.font = '15px monospace'
      ctx.textAlign = 'left'
      ireLevels.forEach((ire, index) => {
        const pixel = keyPixelValues[index]
        const y = scopeHeight - 1 - Math.floor((pixel / 255) * scopeHeight)
        ctx.fillText(`${ire}`, 5, y - 2)
      })

      // IRE labels on left (additional lines at 80% size, 70% opacity)
      ctx.font = '12px monospace'
      ctx.globalAlpha = 0.7
      additionalIreLevels.forEach((ire, index) => {
        const pixel = additionalPixelValues[index]
        const y = scopeHeight - 1 - Math.floor((pixel / 255) * scopeHeight)
        ctx.fillText(`${ire}`, 5, y - 2)
      })
      ctx.globalAlpha = 1

      // Draw ruler line if set
      if (rulerY !== null) {
        ctx.strokeStyle = '#ff8800'
        ctx.lineWidth = 1
        ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(0, rulerY)
        ctx.lineTo(scopeWidth, rulerY)
        ctx.stroke()
        ctx.setLineDash([])
      }

      // Title
      ctx.fillStyle = '#fff'
      ctx.font = 'bold 14px monospace'
      ctx.textAlign = 'left'
      ctx.fillText('Luma', 10, 20)

      animationFrameRef.current = requestAnimationFrame(drawLumaScope)
    }

    drawLumaScope()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [videoRef, imageRef, useSMPTE, isActive, seekTime, brightness, rulerY, canvasSize])

  // Handle canvas click to set ruler
  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    // Get the canvas bounding rect to account for CSS scaling
    const rect = canvas.getBoundingClientRect()
    const scaleY = canvas.height / rect.height
    
    // Scale the click position to match canvas coordinate system
    const y = (e.clientY - rect.top) * scaleY
    
    if (y >= 0 && y < canvas.height) {
      setRulerY(y)
    }
  }

  return (
    <div className="luma-scope" ref={containerRef}>
      <div className="luma-scope-header">
        <h2>Luma Scope</h2>
        <div className="header-controls">
          {rulerY !== null && (
            <button
              className="clear-ruler-button"
              onClick={() => setRulerY(null)}
              title="Clear ruler"
            >
              CLEAR RULER
            </button>
          )}
          <label className="brightness-control">
            <span>Brightness:</span>
            <input
              type="range"
              min="0.1"
              max="20"
              step="0.1"
              value={brightness}
              onChange={(e) => setBrightness(parseFloat(e.target.value))}
              className="brightness-slider"
            />
            <span className="brightness-value">{brightness.toFixed(1)}</span>
          </label>
          <button
            className="toggle-button"
            onClick={() => setIsActive(!isActive)}
          >
            {isActive ? 'Pause' : 'Resume'}
          </button>
          {onToggle2X && (
            <button
              className={`toggle-button ${is2X ? 'active' : ''}`}
              onClick={onToggle2X}
              title="2X Width (Desktop only)"
            >
              2X
            </button>
          )}
        </div>
      </div>
      <canvas
        ref={canvasRef}
        className="luma-scope-canvas"
        onClick={handleCanvasClick}
        style={{ cursor: 'crosshair' }}
      />
    </div>
  )
}

export default LumaScope

